/**
 * M2 — orthogonal opportunity scoring (independent of risk composite).
 */
import type { SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { effectivePhases, effectiveTimeline } from '../projectIntent';
import { PORTFOLIO_CONFIG } from './portfolioConfig';

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export interface OpportunityComponent {
  score: number;
  weight: number;
  rationale: string;
  citations: string[];
  confidence: number;
}

export interface OpportunityScore {
  score: number;
  confidence: number;
  components: {
    capacityScale: OpportunityComponent;
    marketDepth: OpportunityComponent;
    strategicTiming: OpportunityComponent;
    interconnectionUpside: OpportunityComponent;
    expansionPotential: OpportunityComponent;
  };
  rationale: string;
}

function scoreCapacityScale(capacityMW: number): OpportunityComponent {
  const score = capacityMW >= 200 ? 4 : capacityMW >= 50 ? 3 : capacityMW > 0 ? 2 : 1;
  return {
    score,
    weight: PORTFOLIO_CONFIG.OPPORTUNITY_WEIGHT_CAPACITY,
    rationale: `Capacity scale ${capacityMW.toFixed(0)} MW (FOR CALIBRATION bands)`,
    citations: [],
    confidence: capacityMW > 0 ? 0.75 : 0.4,
  };
}

function textBlob(reports: SiteRagReport[], evidence?: SiteEvidenceDocument | null): string {
  const reportText = reports
    .map(r =>
      [
        r.summary,
        r.executiveSummary,
        r.insights?.powerStrategy,
        r.insights?.regulatoryOutlook,
        ...(r.risks?.map(risk => (risk as { title?: string; label?: string }).title ?? risk.label) ?? []),
      ]
        .filter(Boolean)
        .join(' '),
    )
    .join(' ');
  const grid = evidence?.grid?.data ? JSON.stringify(evidence.grid.data) : '';
  return `${reportText} ${grid}`.toLowerCase();
}

function scoreMarketDepth(reports: SiteRagReport[], evidence?: SiteEvidenceDocument | null): OpportunityComponent {
  const blob = textBlob(reports, evidence);
  let score = 2.5;
  if (/load growth|high demand|hyperscale market|major utility/.test(blob)) score = 4;
  else if (/moderate demand|growing market/.test(blob)) score = 3;
  else if (/limited market|rural|sparse/.test(blob)) score = 2;
  return {
    score,
    weight: PORTFOLIO_CONFIG.OPPORTUNITY_WEIGHT_MARKET,
    rationale: 'Market depth from grid/utility signals (FOR CALIBRATION)',
    citations: [],
    confidence: blob.length > 100 ? 0.65 : 0.4,
  };
}

function scoreStrategicTiming(site: SiteEntity, reports: SiteRagReport[]): OpportunityComponent {
  const months = effectiveTimeline(site.projectIntent)?.monthsAway ?? null;
  let score = 3;
  if (months != null) {
    if (months <= 24) score = 4;
    else if (months <= 60) score = 3;
    else score = 2;
  }
  const blob = reports.map(r => r.summary ?? '').join(' ').toLowerCase();
  if (/permit granted|approved|cod confirmed/.test(blob)) score = Math.min(5, score + 0.5);
  return {
    score: clamp(score, 1, 5),
    weight: PORTFOLIO_CONFIG.OPPORTUNITY_WEIGHT_TIMING,
    rationale: months != null
      ? `Timeline ~${Math.round(months)} months to COD (FOR CALIBRATION)`
      : 'Timeline undecided — neutral timing score',
    citations: [],
    confidence: months != null ? 0.7 : 0.45,
  };
}

function scoreInterconnectionUpside(reports: SiteRagReport[], evidence?: SiteEvidenceDocument | null): OpportunityComponent {
  const blob = textBlob(reports, evidence);
  let score = 2.5;
  if (/headroom|upgrade path|transmission expansion|queue advance/.test(blob)) score = 4;
  else if (/moderate queue|standard interconnection/.test(blob)) score = 3;
  else if (/constrained|no headroom|long queue/.test(blob)) score = 2;
  return {
    score,
    weight: PORTFOLIO_CONFIG.OPPORTUNITY_WEIGHT_IX_UPSIDE,
    rationale: 'Interconnection upside from IX/grid narrative (FOR CALIBRATION)',
    citations: [],
    confidence: 0.55,
  };
}

function scoreExpansionPotential(site: SiteEntity): OpportunityComponent {
  const phases = effectivePhases(site.projectIntent);
  const capacityMW = phases.totalCapacityMW ?? 0;
  const acres = Number(site.siteArea) || 0;
  const acresPerMw = capacityMW > 0 && acres > 0 ? acres / capacityMW : null;
  let score = 2.5;
  if (phases.phases.length > 1) score = 4;
  else if (acresPerMw != null && acresPerMw >= 5) score = 3.5;
  else if (acresPerMw != null && acresPerMw >= 2) score = 3;
  else if (capacityMW > 0 && capacityMW < 50) score = 2;
  return {
    score: clamp(score, 1, 5),
    weight: PORTFOLIO_CONFIG.OPPORTUNITY_WEIGHT_EXPANSION,
    rationale: phases.phases.length > 1
      ? `${phases.phases.length} phased expansion blocks`
      : acresPerMw != null
        ? `${acresPerMw.toFixed(1)} acres/MW parcel headroom`
        : 'Expansion potential unknown',
    citations: [],
    confidence: phases.phases.length > 1 || acresPerMw != null ? 0.65 : 0.4,
  };
}

export function computeOpportunityScore(
  site: SiteEntity,
  reports: SiteRagReport[],
  evidence?: SiteEvidenceDocument | null,
): OpportunityScore {
  const phases = effectivePhases(site.projectIntent);
  const capacityMW = phases.totalCapacityMW ?? 0;

  const components = {
    capacityScale: scoreCapacityScale(capacityMW),
    marketDepth: scoreMarketDepth(reports, evidence),
    strategicTiming: scoreStrategicTiming(site, reports),
    interconnectionUpside: scoreInterconnectionUpside(reports, evidence),
    expansionPotential: scoreExpansionPotential(site),
  };

  let sum = 0;
  let w = 0;
  let confSum = 0;
  let n = 0;
  for (const c of Object.values(components)) {
    sum += c.score * c.weight;
    w += c.weight;
    confSum += c.confidence;
    n += 1;
  }

  const score = w > 0 ? clamp(sum / w, 0, 5) : 3;
  const confidence = n > 0 ? confSum / n : 0.5;

  return {
    score,
    confidence,
    components,
    rationale: `Weighted opportunity ${score.toFixed(2)} across 5 dimensions (FOR CALIBRATION).`,
  };
}

/** Map OpportunityScore components to SiteComposite opportunityBreakdown shape. */
export function opportunityBreakdownFromScore(opp: OpportunityScore): SiteCompositeOpportunityBreakdown {
  return {
    capacityScale: opp.components.capacityScale,
    marketDepth: opp.components.marketDepth,
    strategicTiming: opp.components.strategicTiming,
    interconnectionUpside: opp.components.interconnectionUpside,
    expansionPotential: opp.components.expansionPotential,
  };
}

type SiteCompositeOpportunityBreakdown = {
  capacityScale: OpportunityComponent;
  marketDepth: OpportunityComponent;
  strategicTiming: OpportunityComponent;
  interconnectionUpside: OpportunityComponent;
  expansionPotential: OpportunityComponent;
};
